import { Component, OnInit, ViewChild } from "@angular/core";
import { VTabComponent } from "@app/directives/_layout/v-tabs/v-tab.component";
import { VTabsComponent } from "@app/directives/_layout/v-tabs/v-tabs.component";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IAccountGroupConfigDto, ILocalTimeSettings } from "@app/main/modules/foundation/_models/_camera-configs/CameraConfigModels";
import { CustomTriggerMetrics } from "@app/main/modules/foundation/_routes/streamax-configs/custom-trigger-metrics/custom-trigger-metrics.component";
import { ConfigAccountGroupService } from "@app/main/modules/foundation/_services/config-account-group.service";
import { FoundationVehiclesService } from "@app/main/modules/foundation/_services/vehicle.service";
import { IAccount } from "@app/services/account.service";
import { StreamaxCameraParametersService } from "@app/services/camera/streamax-camera-parameters.service";
import { ICameraDeviceParameterProfileSettings, IStreamaxCameraProfile, ITriggerMetricsOverride, StreamaxCameraProfileService } from "@app/services/camera/streamax-camera-profile-service";
import { NavigationService } from "@app/services/navigation.service";
import { PreferencesService } from "@app/services/preferences.service";
import { ITimeZoneInfo } from "@app/userPreferences/model/ITimeZoneInfo";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, from } from "rxjs";
import { tap } from "rxjs/operators";
import './streamax-configs-account-view.component.scss';

@UntilDestroy()
@Component({
    selector: 'streamax-configs-account-view',
    template: require('./streamax-configs-account-view.component.html')
})
export class StreamaxConfigsComponent implements OnInit {
    accountId: number;
    accountGroupConfigs: IAccountGroupConfigDto;
    accounts: IAccount[];
    vehiclesList: IVehicle[];
    profile: IStreamaxCameraProfile;
    
    loading = false;

    timezones: ITimeZoneInfo[];
    updatedTimezone: ITimeZoneInfo;
    updatedStreamaxTimeSettings: ILocalTimeSettings;

    showProfileEditor: boolean = false;

    overriddenVehicleTypes: VehicleType[] = [];

    selectedTab: VTabComponent;

    profileIsSaveable: boolean;

    @ViewChild('customTriggerMetrics') customTriggerMetrics: CustomTriggerMetrics;
    @ViewChild('tabs') tabs: VTabsComponent;
    @ViewChild('devicesList') devicesList: VTabComponent;

    constructor(
        private readonly configAccountGroupService: ConfigAccountGroupService,
        private readonly streamaxCameraParametersService: StreamaxCameraParametersService,
        private readonly navigationService: NavigationService,
        private readonly foundationVehicleService: FoundationVehiclesService,
        private readonly streamaxCameraProfileService: StreamaxCameraProfileService,
        private readonly preferencesService: PreferencesService
    ) { }

    ngOnInit(): void {
        this.accountId = this.navigationService.getRouteParam('accountId');
        
        this.loadData();
    }

    onSelectedTabChanged(tab: VTabComponent): void {
        this.selectedTab = tab;
    }

    toggleProfileEditor(): void {
        this.tabs.selectTab(this.devicesList);
    }

    toggleActiveSet(): void {
        this.profile.IsActive = !this.profile.IsActive;

        this.streamaxCameraProfileService.postActiveSetUpdate(this.profile.Id, this.profile.IsActive).pipe(untilDestroyed(this)).subscribe({
            next: () => this.streamaxCameraProfileService.updateProfileToCache(this.profile)
        });        
    }

    reassessRecords(): void {
        this.updateProfileAndReassessRecords(() => {
            return;
        });
    }

    profileIsInSavableState($event: boolean): void {
        this.profileIsSaveable = $event;
    }

    onCustomTriggerUpdated($event: ITriggerMetricsOverride): void {
        this.updateProfileAndReassessRecords(() => {
            const index = this.profile.CameraDeviceParameterProfileSettings.AlarmProfileMap.TriggerMetricOverrides
                .findIndex(o => o.VehicleType === $event.VehicleType);

            if (index !== -1) {
                this.profile.CameraDeviceParameterProfileSettings.AlarmProfileMap.TriggerMetricOverrides[index] = $event;
            } else {
                this.profile.CameraDeviceParameterProfileSettings.AlarmProfileMap.TriggerMetricOverrides.push($event);
            }

            this.streamaxCameraProfileService.updateProfileToCache(this.profile);
        });
    }

    onCustomTriggerDeleted($event: ITriggerMetricsOverride): void {
        const shouldMoveTab = false;

        this.updateProfileAndReassessRecords(() => {
            const index = this.profile.CameraDeviceParameterProfileSettings.AlarmProfileMap.TriggerMetricOverrides
                .findIndex(o => o.VehicleType === $event.VehicleType);

            if (index !== -1) {
                this.profile.CameraDeviceParameterProfileSettings.AlarmProfileMap.TriggerMetricOverrides =
                    this.profile.CameraDeviceParameterProfileSettings.AlarmProfileMap.TriggerMetricOverrides.filter(o => o.VehicleType !== $event.VehicleType);
            }

            this.streamaxCameraProfileService.updateProfileToCache(this.profile);
        }, shouldMoveTab);
    }

    saveProfile(): void {
        this.updateProfileAndReassessRecords(() => {
            this.profile.CameraDeviceParameterProfileSettings.internalTimezoneInfo = this.profile.CameraDeviceParameterProfileSettings.updatedTimezoneInfo;
            this.profile.CameraDeviceParameterProfileSettings.TimeSettings = this.updatedStreamaxTimeSettings;

            this.streamaxCameraProfileService.postProfile(this.profile).pipe(untilDestroyed(this)).subscribe();
        });
    }

    onProfileUpdated($event: ICameraDeviceParameterProfileSettings): void {
        this.streamaxCameraProfileService.convertTimezoneInfoIdToStreamaxModel(this.profile.CameraDeviceParameterProfileSettings.internalTimezoneInfo.Id).pipe(untilDestroyed(this)).subscribe({
            next: streamaxTimezoneModel => {
                this.profile.CameraDeviceParameterProfileSettings = $event;
                this.updatedStreamaxTimeSettings = streamaxTimezoneModel
            }
        });        
    }

    private updateProfileAndReassessRecords(updateProfileFn: () => void, selectTab: boolean = true): void {
        this.loading = true;

        if (selectTab) {
            this.tabs.selectTab(this.devicesList);
        }

        updateProfileFn();

        // Reassess the records and update the accountGroupConfigs
        this.streamaxCameraParametersService.reassessRecordDeviations(this.profile).pipe(untilDestroyed(this)).subscribe({
            next: updatedRecords => {
                this.accountGroupConfigs = {
                    ...this.accountGroupConfigs,
                    PeripheralWithConfigRecord: this.accountGroupConfigs.PeripheralWithConfigRecord.map(peripheralWithConfig => {
                        const updatedRecord = updatedRecords.find(
                            ur => ur.Id === peripheralWithConfig.LatestRecord?.Id
                        );

                        return {
                            ...peripheralWithConfig,
                            LatestRecord: updatedRecord || peripheralWithConfig.LatestRecord // Update record if found, keep original otherwise
                        };
                    })
                };
            }, complete: () => {
                this.loading = false;
            }
        });
    }

    private loadData(): void {
        this.loading = true;

        combineLatest([
            this.configAccountGroupService.getConfigAccountGroups(),
            this.foundationVehicleService.getVehicles(),
            this.streamaxCameraProfileService.getProfiles(),
            from(this.preferencesService.getTimezones(true) as Promise<ITimeZoneInfo[]>)
        ]).pipe(untilDestroyed(this),
            tap(([accountGroups, vehicles, profiles, timezones]) => {
                this.vehiclesList = vehicles;
                this.accountGroupConfigs = accountGroups.find(a => a.AccountId == this.accountId);        
                this.timezones = timezones;
                this.profile = profiles.find(p => p.AccountId == this.accountId);
                this.overriddenVehicleTypes = this.profile?.CameraDeviceParameterProfileSettings?.AlarmProfileMap?.TriggerMetricOverrides?.map(o => o.VehicleType) ?? [];

                this.loading = false;
            })).subscribe();
    }

    runBuilds(): void {
        this.streamaxCameraParametersService.runTestBuild().subscribe();
    }
}
